import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
    calculateDefaultWidgetHeight,
    LauncherSize,
    ModuleBarSkins,
    setWidgetHeight
} from '@conversion-cloud-widget/UI';
import { Events, subscribe } from '@conversion-cloud-widget/pubsub';
import { animations, animationStyles, animationKeyFrames } from 'constants';

const GlobalModuleBar = styled.div`
    border-radius: 4px;
    margin-left: 4px;
    margin-right: 4px;
    position: relative;
    z-index: 9999;

    &.engaged {
        background-color: transparent;

        .clx-module__launcher {
            margin: 2px 0;
        }

        &.bottom.right {
            margin-left: -328px;
        }

        &.mobile.skin1.bottom.right {
            .clx-container__module-bar__wrapper {
                .clx-step {
                    left: 0;
                }
            }
        }
    }

    &.engaged .clx-container__module-bar__wrapper {
        overflow: visible;
    }

    .clx-container__module-bar__wrapper .clx-module__launcher {
        border-radius: 2px !important;

        .clx-module__launcher-icon {
            margin: 0 4px;
        }
    }
`;

const DefaultModuleBar = styled(GlobalModuleBar)`
    background-color: ${props => props.backgroundColor};
    border-radius: ${props => props.borderRadius};
    border: 2px solid ${props => props.border};
    color: ${props => props.textColor};
    display: flex;
    transition: all 0.25s ease;
    height: ${props => props.barHeight}px;
    -webkit-animation-delay: 2s;
    animation-delay: 2s;
    

    ${props => animations[props.animation]}
    ${props => props.animationStyle}

    animation: animation 7s linear infinite;
    @keyframes animation {
        ${props => animationKeyFrames[`${props.animation}KeyFrame`]}
    }

    ${props => props.marginTop}

    .clx-module__launcher {
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    .clx-container__module-bar__wrapper {
        height: 100%;
    }

    .bottom {
        margin-top: -16px;
    }

    /* Default theme sizes based on setting in admin */
    &.small {
        height: 82px;
        padding: 0 !important;

        .clx-module__launcher {
            height: 78px;
            width: 88px;
        }
    }

    &.medium {
        height: 88px;

        .clx-module__launcher {
            height: 84px;
            width: 104px;
        }
    }

    &.large {
        height: 104px;

        .clx-module__launcher {
            height: 100px;
            width: 120px;

            .clx-module__launcher-icon svg {
                height: 24px;
                width: 24px;
            }
        }
    }

    .clx-container__module-bar__wrapper {
        height: 100%;
        display: flex;
    }

    &.multiple {
        display: flex;

        .clx-container__module-bar__wrapper {
            display: block;
            overflow: hidden;
        }
    }

    &.grid,
    &.opened {
        .clx-container__module-bar__wrapper {
            display: grid;
            grid-template-columns: 1fr 1fr;
        }
    }
    &.engaged {
        .clx-container__module-bar__wrapper {
            display: grid;
            grid-template-columns: none;
        }
    }

    &.grid {
        &.small {
            /* 72 * 2 rows + 2px margin * 4 + 8px */
            height: 160px;

            &.bottom {
                margin-top: -168px;
            }
        }
        &.medium {
            /* 88 * 2 rows + 2px margin * 4 + 8px */
            height: 188px;

            &.bottom {
                margin-top: -200px;
            }
        }
        &.large {
            /* 104 * 2 rows + 2px margin * 4 + 8px */
            height: 224px;

            &.bottom {
                margin-top: -232px;
            }
        }
    }

    &.opened {
        height: auto;

        .clx-container__module-bar__more {
            svg {
                transform: rotate(180deg);
            }
        }
    }

    &:not(.opened):not(.grid) {
        &.bottom {
            &.small {
                margin-top: -80px;
            }

            &.medium {
                margin-top: -96px;
            }

            &.large {
                margin-top: -112px;
            }
        }
    }

    @media (prefers-reduced-motion) {
        .${props => props.animationStyle} {
            -webkit-animation: unset !important;
            -webkit-transition: none !important;
            animation: unset !important;
            transition: none !important;
        }
    }
`;

const WalletModuleBar = styled(GlobalModuleBar)`
    background-color: #ffffff;
    border: 1px solid #f0f0f0;
    color: #181818;
    display: flex;
    height: 64px;
    max-width: 342px;
    -webkit-animation-delay: 2s;
    animation-delay: 2s;

    ${props => animations[props.animation]}
    ${props => props.animationStyle}
    ${props => props.marginTop}

    &.bottom {
        margin-top: -88px;
    }

    &.large {
        .clx-module__launcher {
            .clx-module__launcher-icon svg {
                height: 24px;
                width: 24px;
            }
        }
    }

    .clx-container__module-bar__header {
        display: none;
    }

    .clx-container__module-bar__wrapper {
        display: block;
        height: 64px;
        min-width: 240px;
        position: relative;

        &.engaged {
            min-width: 0;
        }

        .clx-module__launcher {
            .clx-module__launcher-caret {
                display: none;
            }

            &.animate {
                animation: launcher-shuffle 0.5s 0.5s 1;
                z-index: 2;
            }

            &.animated {
                z-index: -1;
            }

            &.next {
                z-index: 1;
                transition: z-index 2s ease;
            }
        }
    }

    @keyframes launcher-shuffle {
        0% {
            border: 1px solid #f0f0f0;
            box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.16);
            z-index: 10;
            transform: translateY(0px) rotate(-2deg);
        }
        50% {
            border: 1px solid #f0f0f0;
            box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.08);
            transform: translateY(-120%) rotate(-4deg) translateX(-4px);
        }
        80% {
            border: 1px solid #f0f0f0;
            box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.16);
            z-index: -1;
        }
        100% {
            border: none;
            box-shadow: none;
            transform: translateY(-40px) rotate(2deg) translateX(4px) scale(0.96);
        }
    }

    .clx-container__module-bar__footer {
        display: none;
    }

    &.opened {
        border: none;
        height: 480px !important;
        display: grid !important;
        grid-template-rows: auto 1fr 64px;
        transition: all 0.25s ease;

        .animate {
            -webkit-animation: unset !important;
            -webkit-transition: none !important;
            animation: unset !important;
            transition: none !important;
        }

        @media only screen and (min-device-width: 320px) and (max-device-width: 480px) {
            width: 100%;
        }

        .clx-container__module-bar__more {
            display: none;
        }

        .clx-container__module-bar__wrapper {
            background: none;
            height: 100%;
            margin-top: -24px;
            overflow-y: auto;
            z-index: 1;

            .clx-module__launcher {
                border: 1px solid #f0f0f0;
                box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.16);
                position: relative;
                margin: 4px auto;
                width: 90%;
                z-index: auto;

                .clx-module__launcher-caret {
                    display: block;
                }
            }
        }

        .clx-container__module-bar__header {
            border-top-left-radius: 4px;
            border-top-right-radius: 4px;
            border-bottom-right-radius: 32%;
            border-bottom-left-radius: 32%;
            font-family: 'gilroy', sans-serif;
            display: block;
            padding: 24px 24px 32px;
            text-align: left;

            .clx-container__module-bar__header-title,
            h1 {
                color: ${props => props.textColor};
                font-size: 24px;
                line-height: 1;
                margin: 0 auto 8px;
                text-transform: none;
            }

            .clx-container__module-bar__header-text {
                margin: 8px 0;
                max-width: 30ch;
            }

            .clx-container__module-bar__header-close {
                text-align: right;

                button {
                    margin-left: auto;
                }
            }
        }

        .clx-container__module-bar__footer {
            border-top: 1px solid #f8f8f8;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'gilroy', sans-serif;
            font-size: 14px;
            padding: 16px;
            text-align: center;
        }
    }

    &.engaged {
        background-color: transparent;
        height: 0;
        transition: all 0 ease;

        .clx-container__module-bar__wrapper {
            overflow-y: visible;
        }

        .clx-container__module-bar__header {
            display: none;
        }

        .clx-container__module-bar__footer {
            display: none;
        }
    }

    &.opened {
        -webkit-animation: unset !important;
        -webkit-transition: none !important;
        animation: unset !important;
        transition: none !important;
    }

    @media (prefers-reduced-motion) {
        .${props => props.animationStyle} {
            -webkit-animation: unset !important;
            -webkit-transition: none !important;
            animation: unset !important;
            transition: none !important;
        }
    }
`;

const ModuleBarThemer = ({
    children,
    backgroundColor,
    borderRadius,
    className,
    mode,
    moduleCount,
    skin,
    textColor,
    yPosition,
    height,
    size,
    widgetExpanded,
    containsConcessionManager,
    animation
}) => {
    const engagedRef = useRef();
    const [engaged, setEngaged] = useState(false);
    let interval;
    let launcherToAnimate;
    let nextLauncherToAnimate;
    let animationEndEvent;
    let marginTop;

    if (skin === ModuleBarSkins.WALLET) {
        marginTop = 'margin-top: 0;';
    } else {
        if (yPosition === 'bottom') {
            if (!!widgetExpanded && yPosition === 'bottom' && moduleCount > 4) {
                const rowCount = Math.ceil(moduleCount / 2);
                marginTop = `margin-top: -${LauncherSize[size]?.height * rowCount + rowCount * 2 + 16}px;`;
            }
        } else {
            marginTop = `margin-top: ${height}px`;
        }
    }
    const barHeight = height;

    const getAnimationEvent = () => {
        const testElement = document.createElement('div');
        const animationEventNames = {
            WebkitAnimation: 'webkitAnimationEnd',
            MozAnimation: 'animationEnd',
            OAnimation: 'oAnimationEnd oanimationend',
            msAnimation: 'MSAnimationEnd',
            animation: 'animationend'
        };

        for (let animation in animationEventNames) {
            if (testElement.style[animation] !== undefined) {
                return (animationEndEvent = animationEventNames[animation]);
            }
        }
    };

    const addClass = (el, cssClass) => {
        el.classList ? el.classList.add(cssClass) : (el.className += ` ${cssClass}`);
    };

    const removeClass = (el, cssClass) => {
        el.classList ? el.classList.remove(cssClass) : el.className.replace(cssClass, '');
    };

    const animationStyle = animation !== '' ? `&${animationStyles[`${animation}Style`]}` : '';

    const animateLaunchers = () => {
        let animationIndex = 0;

        // TODO: Make this configurable in the dashboard
        const ANIMATION_INTERVAL = 10000;

        interval = window.setInterval(() => {
            const moduleLaunchers = document.querySelectorAll('.clx-module__launcher');

            if (moduleLaunchers.length <= 1) {
                return;
            }

            // Add the animate class to the first launcher found
            for (let i = 0; i < moduleLaunchers.length; i++) {
                if (!moduleLaunchers[i].classList.contains('animated')) {
                    launcherToAnimate = moduleLaunchers[i];
                    nextLauncherToAnimate = moduleLaunchers[i + 1];
                    break;
                }
            }

            if (launcherToAnimate) {
                addClass(launcherToAnimate, 'animate');

                // If there isn't a next launcher in the list, we're back at the front of the line
                if (!nextLauncherToAnimate) {
                    nextLauncherToAnimate = moduleLaunchers[0];
                }

                addClass(nextLauncherToAnimate, 'next');
            } else {
                [...moduleLaunchers].forEach(launcher => removeClass(launcher, 'animated'));

                launcherToAnimate = moduleLaunchers[0];
                nextLauncherToAnimate = moduleLaunchers[1];

                addClass(launcherToAnimate, 'animate');
                addClass(nextLauncherToAnimate, 'next');
            }

            launcherToAnimate.addEventListener(
                animationEndEvent,
                () => {
                    removeClass(launcherToAnimate, 'animate');
                    removeClass(launcherToAnimate, 'next');
                    if (!launcherToAnimate.classList.contains('animated')) {
                        addClass(launcherToAnimate, 'animated');
                    }
                },
                false
            );

            animationIndex += 1;
            if (animationIndex === moduleLaunchers.length) {
                [...moduleLaunchers].forEach(launcher => {
                    removeClass(launcher, 'animated');
                    removeClass(launcher, 'next');
                });
            }
        }, ANIMATION_INTERVAL);
    };

    useEffect(() => {
        subscribe(Events.MODULE_OPENED, () => {
            const moduleBar = document.querySelector('#clx-container__module-bar');
            const moduleBarWrapper = document.querySelector('.clx-container__module-bar__wrapper');
            moduleBarWrapper.classList.add('engaged');
            moduleBar.removeAttribute('style');
            moduleBar.classList.remove('opened');
            engagedRef.current.classList.add('engaged');
            moduleBar.parentNode.classList.add('engaged');
            setEngaged(true);
        });
        subscribe(Events.MODULE_CLOSED, () => {
            const moduleBar = document.querySelector('#clx-container__module-bar');
            const moduleBarFrame = document.querySelector('#clx-container__module-bar-frame');
            const moduleBarWrapper = document.querySelector('.clx-container__module-bar__wrapper');

            /* Edge Case: If the module was opened from a responsive media query point, we need to re-apply the top margin, if the widget is the default theme and aligned bottom */
            if (skin === ModuleBarSkins.DEFAULT && yPosition === 'bottom') {
                let mq = window.matchMedia('only screen and (max-width: 56rem)');

                if (mq.matches) {
                    const widgetHeight = calculateDefaultWidgetHeight() || 0;

                    if (widgetHeight <= 0) {
                        return;
                    }

                    moduleBar.style.marginTop = `-${widgetHeight + 8}px`;
                }
            }

            moduleBarWrapper.classList.remove('engaged');
            moduleBarFrame.classList.remove('engaged');
            engagedRef.current.classList.remove('engaged');
            engagedRef.current.classList.remove('opened');
            setEngaged(false);
        });
        subscribe('WALLET_MORE_OPEN', () => {
            [...document.querySelectorAll('.clx-wallet-stack')].forEach(elem =>
                elem.setAttribute('style', 'display:none')
            );
            if (interval) {
                window.clearInterval(interval);
            }
        });
        subscribe('WALLET_MORE_CLOSE', () => {
            if (skin === ModuleBarSkins.WALLET && moduleCount > 1) {
                [...document.querySelectorAll('.clx-wallet-stack')].forEach(elem =>
                    elem.setAttribute('style', 'display:block')
                );
                animateLaunchers();
            }
        });
        subscribe(Events.OFFERS_LOADED, payload => {
            if (skin === ModuleBarSkins.WALLET && widgetExpanded) {
                setWidgetHeight(yPosition, skin, 64);
            }
        });

        if (skin === ModuleBarSkins.WALLET && moduleCount > 1) {
            getAnimationEvent();
            animateLaunchers();
        }

        if (skin === ModuleBarSkins.WALLET && widgetExpanded && !containsConcessionManager) {
            setWidgetHeight(yPosition, skin, 64);
        }
    }, []);

    return skin === ModuleBarSkins.WALLET ? (
        <WalletModuleBar
            id="clx-container__module-bar"
            className={className}
            data-testid="clx-container__module-bar"
            animation={animation}
            animationStyle={animationStyle}
            backgroundColor={backgroundColor}
            marginTop={marginTop}
            ref={engagedRef}
            textColor={textColor}
            yPosition={yPosition}
        >
            {children}
        </WalletModuleBar>
    ) : (
        <DefaultModuleBar
            id="clx-container__module-bar"
            className={className}
            data-testid="clx-container__module-bar"
            animation={animation}
            animationStyle={animationStyle}
            backgroundColor={mode === 'light' ? '#ffffff' : backgroundColor}
            barHeight={barHeight}
            border={backgroundColor}
            borderRadius={borderRadius}
            marginTop={marginTop}
            ref={engagedRef}
            textColor={mode === 'light' ? backgroundColor : textColor}
            yPosition={yPosition}
        >
            {children}
        </DefaultModuleBar>
    );
};

export default ModuleBarThemer;
